import React, { useEffect, useState } from "react";
import StreamStatusScreen, {
  type StreamStatusScreenProps,
} from "./StreamStatusScreen";

interface CountdownProps
  extends Pick<
    StreamStatusScreenProps,
    "variant" | "courtLabel" | "matterLabel"
  > {
  streamStartAt: string;
  onCountdownEnd: () => void;
}

const pad = (n: number) => n.toString().padStart(2, "0");

const StreamCountdown: React.FC<CountdownProps> = ({
  streamStartAt,
  onCountdownEnd,
  ...screenProps
}) => {
  const [timeLeft, setTimeLeft] = useState({ h: 0, m: 0, s: 0 });
  const [isEnded, setIsEnded] = useState(false);

  useEffect(() => {
    const target = new Date(streamStartAt).getTime();

    const updateCountdown = () => {
      const now = Date.now();
      const diff = target - now;

      if (diff <= 0) {
        setTimeLeft({ h: 0, m: 0, s: 0 });
        if (!isEnded) {
          setIsEnded(true);
          onCountdownEnd();
        }
        return;
      }

      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);

      setTimeLeft({ h, m, s });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [streamStartAt, onCountdownEnd, isEnded]);

  const units = [
    { value: timeLeft.h, label: "Hours" },
    { value: timeLeft.m, label: "Minutes" },
    { value: timeLeft.s, label: "Seconds" },
  ];

  return (
    <StreamStatusScreen status="startingSoon" {...screenProps}>
      <div
        className="mt-4 flex justify-center gap-3 sm:gap-4"
        aria-label={`Starts in ${timeLeft.h} hours ${timeLeft.m} minutes ${timeLeft.s} seconds`}
      >
        {units.map(({ value, label }) => (
          <div
            key={label}
            className="flex min-w-16 flex-col items-center rounded-lg bg-white/5 px-3 py-2"
          >
            <span className="font-mono text-2xl font-semibold text-blue-200 sm:text-3xl">
              {pad(value)}
            </span>
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              {label}
            </span>
          </div>
        ))}
      </div>
    </StreamStatusScreen>
  );
};

export default StreamCountdown;
